import { useCallback, useEffect, useRef, useState } from 'react';
import { Settings } from 'lucide-react';
import { Sizes } from 'enums/sizes';
import { LightContractCard } from 'features/subscription-process/contractualization-process/components/cards/LightContractCard';
import { CardState } from 'features/subscription-process/contractualization-process/types/cardState';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { usePanel } from 'hooks/usePanel';
import { useSingleFlight } from 'hooks/useSingleFlight';
import { NsKey } from 'enums/NsKey';
import { useGroupContractProcess } from '../../contexts/GroupContractProcessContext';
import { SpecialConditionsForm, SpecialConditionsFormHandle } from '../../forms/SpecialConditionsForm';
import { SpecialConditionsLockedSummary } from './SpecialConditionsLockedSummary';
import { ViewDetailsButton } from '../../components/ViewDetailsButton';

interface PanelContentProps {
  groupContractId: string;
  subscriberId: string | null;
  isCadre: boolean;
  formRef: React.RefObject<SpecialConditionsFormHandle>;
  onValidityChange: (isValid: boolean) => void;
  onLockedChange: (isLocked: boolean) => void;
}

const PanelContent = ({
  groupContractId,
  subscriberId,
  isCadre,
  formRef,
  onValidityChange,
  onLockedChange,
}: PanelContentProps) => (
  <SpecialConditionsForm
    ref={formRef}
    groupContractId={groupContractId}
    subscriberId={subscriberId}
    isCadre={isCadre}
    onValidityChange={onValidityChange}
    onLockedChange={onLockedChange}
  />
);

interface SpecialConditionsCardProps {
  /** Notifie le parent quand les CP passent de invalides à valides (ou inversement). */
  onValidityChange?: (isValid: boolean) => void;
}

export const SpecialConditionsCard = ({ onValidityChange }: SpecialConditionsCardProps) => {
  const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common]);
  // Le contrat vient du contexte (source unique, rafraîchie en place par refresh()).
  const { groupContractId, contract, isLocked, refresh } = useGroupContractProcess();
  const { openPanel, closePanel, setPanelActions } = usePanel();

  const formRef = useRef<SpecialConditionsFormHandle>(null);
  const [formValid, setFormValid] = useState(false);

  // CP complète = tous les champs raw renseignés côté back.
  // On utilise les champs raw (numériques / id) plutôt que les libellés formatés
  // (mainDueDate / noticePeriodDuration / periodicity), qui peuvent être présents
  // partiellement même si la CP n'est pas complète.
  const isFilled = !!(
    contract?.effectiveDate
    && contract?.mainDueDateDay != null
    && contract?.mainDueDateMonth != null
    && contract?.periodicityId
    && contract?.noticeDurationValue != null
    && contract?.noticeDurationUnit != null
  );

  // Notifie le parent (pour bloquer les signatures tant que pas complète).
  useEffect(() => {
    onValidityChange?.(isFilled);
  }, [isFilled, onValidityChange]);

  const cardState: CardState = isFilled ? CardState.VALID : CardState.CRITICAL;

  const subtitle = t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS_CARD.SUBTITLE');

  const saveForm = useCallback(async () => {
    if (!formRef.current) return;
    await formRef.current.save();
    await refresh();
    closePanel();
  }, [refresh, closePanel]);

  // Un seul save en vol : les soumissions répétées du panel sont ignorées
  // (sinon PUT /update + /mandat concurrents → conflit de concurrence côté back).
  const { run: handleSave, isInFlight: isSaving } = useSingleFlight(saveForm);

  // Maintient les actions du panel à jour selon validité + état save.
  useEffect(() => {
    setPanelActions(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        onSubmit: handleSave,
        submitDisabled: !formValid || isSaving,
        submitLabel: isSaving
          ? t('GROUP_CONTRACT_PROCESS.ACTIONS.SAVING')
          : t('GROUP_CONTRACT_PROCESS.ACTIONS.SAVE'),
      };
    });
  }, [formValid, isSaving, handleSave, setPanelActions, t]);

  const handleOpen = useCallback(() => {
    // Verrouillé → panel read-only (résumé), sans footer d'actions.
    if (isLocked) {
      openPanel(
        {
          title: t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS_CARD.PANEL_TITLE'),
          size: Sizes['3xl'],
        },
        <SpecialConditionsLockedSummary groupContractId={groupContractId} />,
      );
      setPanelActions(undefined);
      return;
    }

    const subscriberId = (contract?.subscriber as { id?: string } | undefined)?.id ?? null;
    // isMaster (boolean) est la vraie source de vérité côté DTO ; le champ `type` est une string
    // potentiellement désynchronisée selon les versions du back.
    const isCadre = !!contract?.isMaster;
    openPanel(
      {
        title: t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS_CARD.PANEL_TITLE'),
        size: Sizes['3xl'],
      },
      <PanelContent
        groupContractId={groupContractId}
        subscriberId={subscriberId}
        isCadre={isCadre}
        formRef={formRef}
        onValidityChange={setFormValid}
        onLockedChange={() => { /* lock state remonté via le contexte global */ }}
      />,
    );
    setPanelActions({
      onSubmit: handleSave,
      submitLabel: t('GROUP_CONTRACT_PROCESS.ACTIONS.SAVE'),
      closeLabel: t('GROUP_CONTRACT_PROCESS.ACTIONS.CANCEL'),
      submitDisabled: true,
    });
  }, [isLocked, openPanel, setPanelActions, groupContractId, contract, handleSave, t]);

  return (
    <LightContractCard
      className={isLocked ? 'hover:!shadow-none' : undefined}
      icon={<Settings className="w-7 h-7 text-primary" />}
      title={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS_CARD.TITLE')}
      subtitle={subtitle}
      status={cardState}
      onClick={isLocked ? undefined : handleOpen}
      titleAsideOverride={isLocked ? <ViewDetailsButton onClick={handleOpen} /> : undefined}
    />
  );
};
